import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  effect,
  computed,
  input,
  untracked,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { catchError, finalize } from 'rxjs/operators';
import { of, forkJoin, Subscription } from 'rxjs';
import { CommonModule, CurrencyPipe, PercentPipe, DecimalPipe } from '@angular/common';
import { ArrangmentCronApiService } from '../../service/api-service.service';
import { ArrangmentCronDatePickerService } from '../../services/date-picker.service';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { CreationTrendAPI, CreationTrendStats, RecoveryStatusAPI, RecoveryStatusStats, SuccessRateTrendAPI, SuccessRateTrendStats, RecoveryRateAPI, RecoveryRateStats, PaymentOutcomesBySlotAPI, PaymentOutcomesBySlotStats, ProcessingTimeStats, DuplicatesSummaryStatsAPI, RecoveryStatusRecord } from '../../interface/arrangment-cron-api.interface';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { BarLineComponent, BarChartData, LegendData } from '../../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';

import { IsometricBarComponent, IsometricBarData } from '../../../../../shared/components/charts/isometric-bar/isometric-bar.component';
import { StackedBarComponent, StackedBarData } from '../../../../../shared/components/charts/stacked-bar/stacked-bar.component';
import { MultiHorizontalBarChartComponent, MultiBarData } from '../../../../../shared/components/charts/multi-horizontal-bar-chart/multi-horizontal-bar-chart.component';
import { DataProgressBarComponent, ProgressBarData } from '../../../../../shared/components/data-progress-bar/data-progress-bar.component';
import { LollipopChartComponent, LollipopChartData } from '../../../../../shared/components/charts/lollipop-chart/lollipop-chart.component';
import { PolarAreaChartComponent, PolarChartData } from '../../../../../shared/components/charts/polar-area-chart/polar-area-chart.component';
import { parseToUtc } from '../../../../../shared/utils/date-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { IndividualToasterComponent } from '../../../../../shared/components/loader/loader.component';
import { PieChartComponent } from "../../../../../shared/components/charts/pie-chart/pie-chart.component";
import { TrendsCalculationService, TrendUnit } from '../../services/trends-calculation.service';
import { ComprehensiveBarChartComponent, IBarChartSeries } from '../../../../../shared/components/charts/bar-charts/comprehensive-bar-chart/comprehensive-bar-chart.component';
import { ARRANGMENT_CRON_DEFAULT_COL_SPAN } from '../../arrangment-cron-layout.catalog';
import {
  arrangmentSectionColClass,
  arrangmentSectionOrder,
  showArrangmentSection,
} from '../../arrangment-cron-section-layout';
import {
  ArrangementCronTrendsSectionDef,
  ArrangementCronTrendsSectionId,
  ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS,
  DashboardPageKey,
  isArrangementCronTrendsSectionId,
} from '../../../../../core/enums/dashboard-cards.enum';
import {
  createEmbeddedSectionFilterByWidgetKeyMap,
  findCatalogPageId,
  resolveActiveCatalogProductId,
  resolveWidgetKeyToSectionId,
  stripProductPrefix,
} from '../../../../../shared/components/dashboard/dashboard-embed.helpers';
import { CustomDashboardPinService } from '../../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../../../core/services/toaster.service';
import { ApiService } from '../../../../../core/services/api.service';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  normalizeLayoutEditorColSpan,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DashboardLayoutEditorComponent } from '../../../../../shared/components/dashboard/dashboard-layout-editor.component';

dayjs.extend(utc);
dayjs.extend(weekOfYear);
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';

export interface MonthlyPerformanceData {
  month: string;
  recoveredAmount: number;
  rate: number;
  countSuccess: string;
  trend: number;
  trendDirection: 'up' | 'down';
}

export interface YearlyPerformanceData {
  year: number;
  recoveredAmount: number;
  rate: number;
  countSuccess: string;
  trend: number;
  trendDirection: 'up' | 'down';
  monthlyData: MonthlyPerformanceData[];
}

export interface RiskResult {
  label: 'CRITICAL' | 'HIGH' | 'MODERATE' | 'IMPROVING';
  colorClass: string;
}

export interface ChartColumnConfig {
  sectionId: string;
  title: string;
  subtitle?: string;
  type: 'bar-line' | 'pie' | 'isometric' | 'stacked-bar' | 'line' | 'data-progress' | 'lollipop' | 'polar' | 'multi-horizontal-bar' | 'comprehensive-bar';
  loading?: boolean;
  hasData?: boolean;
  data: any;
  config?: any;
  footerType?: 'success-legend' | 'actions' | 'insights' | 'none';
}

export interface ChartRowConfig {
  cols: ChartColumnConfig[];
}

/** Persisted layout state per trends section. */
interface TrendsSectionLayoutItem {
  id: ArrangementCronTrendsSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

/** Draft row used by the Customize Layout editor. */
interface TrendsLayoutEditorItem extends TrendsSectionLayoutItem {
  title: string;
  defaultColSpan?: number | null;
}

@Component({
  selector: 'app-trends',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    CurrencyPipe,
    IconComponentV2,
    BarLineComponent,
    IsometricBarComponent,
    StackedBarComponent,
    LineChartComponent,
    DataProgressBarComponent,
    LollipopChartComponent,
    PolarAreaChartComponent,
    IndividualToasterComponent,
    PieChartComponent,
    MultiHorizontalBarChartComponent,
    ComprehensiveBarChartComponent,
    DashboardLayoutEditorComponent,
  ],
  providers: [CurrencyPipe, PercentPipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './trends.component.html'
})
export class TrendsComponent implements OnInit, OnDestroy {
  private arrangmentCronApiService = inject(ArrangmentCronApiService);
  private datePickerService = inject(ArrangmentCronDatePickerService);
  private trendsService = inject(TrendsCalculationService);
  private decimalPipe = inject(DecimalPipe);
  private percentPipe = inject(PercentPipe);
  private currencyPipe = inject(CurrencyPipe);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private toaster = inject(ToasterService);
  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.ARRANGEMENT_CRON_TRENDS;

  readonly SectionId = ArrangementCronTrendsSectionId;
  get SECTIONS() {
    return ArrangementCronTrendsSectionDef.filter(s =>
      s.id !== ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS || this.mixedMode() || this.criticalInsights().length > 0
    );
  }

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<
    string,
    ArrangementCronTrendsSectionId
  > = new Map(
    (
      Object.entries(ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS) as [
        ArrangementCronTrendsSectionId,
        string,
      ][]
    ).map(([sectionId, backendKey]) => [backendKey, sectionId]),
  );

  readonly layoutMap = signal<
    Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem>
  >({} as Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem>);

  readonly orderedSections = computed(() => {
    const sections = this.SECTIONS;
    const layout = this.layoutMap();
    return sections
      .map((s) => layout[s.id as ArrangementCronTrendsSectionId])
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
  });

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );


  readonly pinnedSections = signal<Set<ArrangementCronTrendsSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<ArrangementCronTrendsSectionId>>(new Set());
  readonly pinnableWidgetIds = signal<Map<ArrangementCronTrendsSectionId, number>>(
    new Map(),
  );

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<TrendsLayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_arrangment_cron_tr_layout_v1';
  private _layoutSnapshot: Record<
    ArrangementCronTrendsSectionId,
    TrendsSectionLayoutItem
  > | null = null;

  trackEditorItem = (_idx: number, item: TrendsLayoutEditorItem): string => item.id;

  showSection(sectionId: string): boolean {
    if (this.mixedMode()) {
      return this.embeddedVisibleSections().some((s) => s.id === sectionId);
    }
    return showArrangmentSection(this._layoutRecord(), sectionId);
  }

  sectionOrder(sectionId: string): number {
    return arrangmentSectionOrder(this._layoutRecord(), sectionId);
  }

  getSectionColClass(sectionId: string): string {
    if (this.mixedMode()) return 'col-span-12';
    return arrangmentSectionColClass(this._layoutRecord(), sectionId);
  }

  private _layoutRecord(): Record<string, TrendsSectionLayoutItem> {
    return this.layoutMap() as unknown as Record<string, TrendsSectionLayoutItem>;
  }

  orderedVisibleCharts = computed(() => {
    const layout = this._layoutRecord();
    const charts = this.chartRows().flatMap((row) => row.cols);

    // In mixedMode (embedded), we only show the pinned widgets, regardless of layout map
    if (this.mixedMode()) {
      return charts.filter((col) => this.showSection(col.sectionId));
    }

    if (!layout || !Object.keys(layout).length) return charts;
    return charts
      .filter((col) => this.showSection(col.sectionId))
      .sort(
        (a, b) =>
          arrangmentSectionOrder(layout, a.sectionId) -
          arrangmentSectionOrder(layout, b.sectionId),
      );
  });


  // Signals for data
  recoveryStatusStats = signal<RecoveryStatusStats | null>(null);
  successRateTrendStats = signal<SuccessRateTrendStats | null>(null);
  paymentOutcomesStats = signal<PaymentOutcomesBySlotStats | null>(null);
  recoveryStatusRecords = signal<RecoveryStatusRecord[]>([]); // New signal for trend data

  // Stats signals for insights
  creationTrendStats = signal<CreationTrendStats | null>(null);
  recoveryRateStats = signal<RecoveryRateStats | null>(null);
  processingTimeStats = signal<ProcessingTimeStats | null>(null);
  duplicatesStats = signal<DuplicatesSummaryStatsAPI | null>(null);

  // Previous period signals for comparison
  previousRecoveryStatusStats = signal<RecoveryStatusStats | null>(null);
  previousSuccessRateTrendStats = signal<SuccessRateTrendStats | null>(null);
  previousCreationTrendStats = signal<CreationTrendStats | null>(null);
  previousRecoveryRateStats = signal<RecoveryRateStats | null>(null);


  // Loading signals
  isRecoveryStatusLoading = signal<boolean>(false);
  isSuccessRateTrendLoading = signal<boolean>(false);
  isRecoveryRateLoading = signal<boolean>(false);
  isPaymentOutcomesLoading = signal<boolean>(false);
  readonly isPreviousDataLoading = signal<boolean>(false);
  readonly isProcessingTimeLoading = signal<boolean>(false);
  isDuplicatesLoading = signal<boolean>(false);
  isRecoveryByAttemptsLoading = signal<boolean>(false);

  // Date range and duration
  startDate = this.datePickerService.startDate;
  endDate = this.datePickerService.endDate;
  unit = this.datePickerService.unit;

  isLoadingCreationTrend = signal<boolean>(false);
  creationTrend = signal<CreationTrendAPI | null>(null);
  duration = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    if (!start || !end) return 0;
    return dayjs(end).diff(dayjs(start), 'day') + 1;
  });

  titlePrefix = computed(() => {
    const unit = this.unit();
    switch (unit) {
      case 'day': return 'Daily';
      case 'week': return 'Weekly';
      case 'month': return 'Monthly';
      case 'quarter': return 'Quarterly';
      case 'year': return 'Yearly';
      default: return 'Period';
    }
  });

  // Dynamic Data Structures
  metricCards = computed(() => {
    const recoveryStats = this.recoveryStatusStats();
    const successStats = this.successRateTrendStats();
    const unit = this.unit() as TrendUnit;

    // Real data for comparison from previous period
    const prevRecoveryStats = this.previousRecoveryStatusStats();
    const prevSuccessStats = this.previousSuccessRateTrendStats();

    const currentNonRecovered = recoveryStats?.total_non_recovered_amount || 0;
    const currentScheduled = recoveryStats?.total_scheduled_amount || 0;
    const prevNonRecovered = prevRecoveryStats?.total_non_recovered_amount || 0;

    const nonRecoveredAmountChange = this.trendsService.calculatePercentageChange(currentNonRecovered, prevNonRecovered);
    const nonRecoveredTrend = this.trendsService.getTrendResult(nonRecoveredAmountChange, unit, false, true, this.duration());

    // Risk Calculation for Non-Recovered with trend awareness
    const risk = this.trendsService.getRiskLevel(
      currentNonRecovered,
      currentScheduled,
      nonRecoveredTrend?.direction
    );

    const recoveredChange = this.trendsService.calculatePercentageChange(
      recoveryStats?.total_recovered_amount || 0,
      prevRecoveryStats?.total_recovered_amount || 0
    );

    const recoveredTrend = this.trendsService.getTrendResult(recoveredChange, unit, false, false, this.duration());

    const successTrend = this.trendsService.getTrendResult(
      this.trendsService.calculatePPChange(
        (successStats?.avg_success_rate || 0) / 100,
        (prevSuccessStats?.avg_success_rate || 0) / 100
      ),
      unit,
      true,
      false,
      this.duration()
    );

    const recoveryRateChange = this.trendsService.calculatePPChange(
      (recoveryStats?.overall_recovery_rate || 0) / 100,
      (prevRecoveryStats?.overall_recovery_rate || 0) / 100
    );

    const recoveryRateTrend = this.trendsService.getTrendResult(recoveryRateChange, unit, true, false, this.duration());

    return [
      {
        title: 'Total Arrangements',
        value: recoveryStats?.total_arrangements || 0,
        type: 'number',
        footer: 'Across all periods'
      },
      {
        title: 'Total Recovered',
        value: recoveryStats?.total_recovered_amount || 0,
        type: 'currency',
        trend: recoveredTrend,
        footer: this.trendsService.getRecoveryFooter(recoveredChange)
      },
      {
        title: 'Non-Recovered',
        value: recoveryStats?.total_non_recovered_amount || 0,
        type: 'currency',
        trend: nonRecoveredTrend,
        footer: risk.label,
        footerClass: risk.colorClass + ' font-medium'
      },
      {
        title: 'Success Rate Trend',
        value: (successStats?.avg_success_rate || 0) / 100,
        type: 'percent',
        trend: successTrend,
        footer: `Avg Recurring: ${((successStats?.avg_recurring_success_rate || 0) / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 1 })}`
      },
      {
        title: 'Recovery Rate %',
        value: (recoveryStats?.overall_recovery_rate || 0) / 100,
        type: 'percent',
        trend: recoveryRateTrend,
        footer: this.trendsService.getRecoveryRateFooter(recoveryRateChange)
      }
    ];
  });

  insightCards = computed(() => {
    const creation = this.creationTrendStats();
    const prevCreation = this.previousCreationTrendStats();
    const success = this.successRateTrendStats();
    const prevSuccess = this.previousSuccessRateTrendStats();
    const recoveryRate = this.recoveryRateStats();
    const prevRecoveryRate = this.previousRecoveryRateStats();
    const unit = this.unit() as TrendUnit;

    const volumeChange = this.trendsService.calculatePercentageChange(
      creation?.total_created || 0,
      prevCreation?.total_created || 0
    );
    const volumeTrend = this.trendsService.getTrendResult(volumeChange, unit, false, false, this.duration());

    const successChange = this.trendsService.calculatePPChange(
      (success?.avg_success_rate || 0) / 100,
      (prevSuccess?.avg_success_rate || 0) / 100
    );
    const successTrend = this.trendsService.getTrendResult(successChange, unit, true, false, this.duration());

    const recoveryChange = this.trendsService.calculatePPChange(
      (recoveryRate?.overall_recovery_rate || 0) / 100,
      (prevRecoveryRate?.overall_recovery_rate || 0) / 100
    );
    const recoveryTrend = this.trendsService.getTrendResult(recoveryChange, unit, true, false, this.duration());

    return [
      {
        title: 'Volume Trend',
        icon: 'chartColumn',
        bgColor: 'bg-rose-50 dark:bg-rose-900/10',
        borderColor: 'border-rose-100 dark:border-rose-800/20',
        iconBg: 'bg-white dark:bg-rose-900/30',
        insightText: volumeTrend ? `Arrangement creation has ${volumeTrend.direction === 'up' ? 'increased' : volumeTrend.direction === 'down' ? 'decreased' : 'remained stable'} compared to the previous period.` : 'Loading volume insights...',
        trendLabel: volumeTrend?.value || '0% Comparison',
        trendIcon: volumeTrend?.icon || 'minus',
        trendColor: volumeTrend?.direction === 'up' ? 'text-emerald-700 dark:text-emerald-400' : volumeTrend?.direction === 'down' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-700 dark:text-slate-400',
        trendBg: volumeTrend?.direction === 'up' ? 'bg-emerald-100 dark:bg-emerald-500/20' : volumeTrend?.direction === 'down' ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-slate-100 dark:bg-slate-500/20',
        fromValue: this.decimalPipe.transform(prevCreation?.total_created || 0) || '0',
        toValue: this.decimalPipe.transform(creation?.total_created || 0) || '0'
      },
      {
        title: 'Success Rate',
        icon: 'circleCheck',
        bgColor: 'bg-orange-50 dark:bg-orange-900/10',
        borderColor: 'border-orange-100 dark:border-orange-800/20',
        iconBg: 'bg-white dark:bg-orange-900/30',
        insightText: successTrend ? `The average success rate has ${successTrend.direction === 'up' ? 'improved' : successTrend.direction === 'down' ? 'declined' : 'stabilized'} by ${successTrend.value.split(' ')[0]} percentage points.` : 'Calculating success trends...',
        trendLabel: successTrend?.value || '0% PP Comparison',
        trendIcon: successTrend?.icon || 'minus',
        trendColor: successTrend?.direction === 'up' ? 'text-emerald-700 dark:text-emerald-400' : successTrend?.direction === 'down' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-700 dark:text-slate-400',
        trendBg: successTrend?.direction === 'up' ? 'bg-emerald-100 dark:bg-emerald-500/20' : successTrend?.direction === 'down' ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-slate-100 dark:bg-slate-500/20',
        fromValue: this.percentPipe.transform((prevSuccess?.avg_success_rate || 0) / 100, '1.1-1') || '0%',
        toValue: this.percentPipe.transform((success?.avg_success_rate || 0) / 100, '1.1-1') || '0%'
      },
      {
        title: 'Recovery Amount',
        icon: 'Target',
        bgColor: 'bg-blue-50 dark:bg-blue-900/10',
        borderColor: 'border-blue-100 dark:border-blue-800/20',
        iconBg: 'bg-white dark:bg-blue-900/30',
        insightText: recoveryTrend ? `Overall recovery performance is ${recoveryTrend.direction === 'up' ? 'trending upwards' : recoveryTrend.direction === 'down' ? 'trending downwards' : 'holding steady'} across this time period.` : 'Analyzing recovery rates...',
        trendLabel: recoveryTrend?.value || '0% Comparison',
        trendIcon: recoveryTrend?.icon || 'minus',
        trendColor: recoveryTrend?.direction === 'up' ? 'text-emerald-700 dark:text-emerald-400' : recoveryTrend?.direction === 'down' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-700 dark:text-slate-400',
        trendBg: recoveryTrend?.direction === 'up' ? 'bg-emerald-100 dark:bg-emerald-500/20' : recoveryTrend?.direction === 'down' ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-slate-100 dark:bg-slate-500/20',
        fromValue: this.percentPipe.transform((prevRecoveryRate?.overall_recovery_rate || 0) / 100, '1.1-1') || '0%',
        toValue: this.percentPipe.transform((recoveryRate?.overall_recovery_rate || 0) / 100, '1.1-1') || '0%'
      }
    ];
  });

  // Intelligent Insights Engine
  criticalInsights = computed(() => {
    const insights: any[] = [];

    // Data Sources
    const currentRecovery = this.recoveryStatusStats();
    const prevRecovery = this.previousRecoveryStatusStats();
    const currentSuccess = this.successRateTrendStats();
    const prevSuccess = this.previousSuccessRateTrendStats();
    const outcomes = this.paymentOutcomesStats();
    const duplicates = this.duplicatesStats();
    const processing = this.processingTimeStats();
    const creation = this.creationTrendStats();
    const prevCreation = this.previousCreationTrendStats();

    // Year Labels
    const start = dayjs(this.startDate());
    const duration = this.duration();
    const currYear = start.format('YYYY');
    const prevYear = start.subtract(duration, 'day').format('YYYY');

    // 1. Volume Scan (Critical)
    if (creation && prevCreation) {
      const drop = this.trendsService.calculatePercentageChange(creation.total_created, prevCreation.total_created);
      if (drop !== null && drop <= -20) {
        insights.push({
          title: 'Volume Decline',
          description: `Arrangements decreased from ${this.decimalPipe.transform(prevCreation.total_created)} (${prevYear}) to ${this.decimalPipe.transform(creation.total_created)} (${currYear}) — a drastic ${Math.abs(drop).toFixed(0)}% reduction needing audit.`,
          type: 'critical'
        });
      }
    }

    // 2. Success Rate Scan (Critical)
    if (currentSuccess && prevSuccess) {
      const diff = (currentSuccess.avg_success_rate - prevSuccess.avg_success_rate) / 100;
      if (diff <= -0.01) { // -1% drop
        insights.push({
          title: 'Declining Success Trend',
          description: `Overall success rate dropped from ${this.percentPipe.transform(prevSuccess.avg_success_rate / 100, '1.1-1')} (${prevYear}) to ${this.percentPipe.transform(currentSuccess.avg_success_rate / 100, '1.1-1')} (${currYear}) — a ~${Math.abs(diff * 100).toFixed(0)}% absolute decline.`,
          type: 'critical'
        });
      }
    }

    // 3. Recovery Gap Scan (Critical)
    if (currentRecovery && currentRecovery.total_scheduled_amount > 0) {
      const gapShare = currentRecovery.total_non_recovered_amount / currentRecovery.total_scheduled_amount;
      if (gapShare > 0.05) {
        insights.push({
          title: 'Recovery Gap',
          description: `${this.currencyPipe.transform(currentRecovery.total_non_recovered_amount, 'USD', 'symbol', '1.1-1')} in non-recovered amount represents ${(gapShare * 100).toFixed(1)}% of total scheduled value. Significant leakage.`,
          type: 'critical'
        });
      }
    }

    // 4. Slot Advantage Scan (Insight)
    if (outcomes && outcomes.by_slot) {
      const slots = [
        { name: 'Morning', rate: outcomes.by_slot.morning.success_rate },
        { name: 'Afternoon', rate: outcomes.by_slot.afternoon.success_rate },
        { name: 'Evening', rate: outcomes.by_slot.evening.success_rate }
      ].sort((a, b) => b.rate - a.rate);

      const best = slots[0];
      const worst = slots[slots.length - 1];

      if (best.rate > worst.rate * 1.25) {
        insights.push({
          title: `${best.name} Slot Advantage`,
          description: `${best.name} arrangements show ${this.percentPipe.transform(best.rate / 100, '1.1-1')} success vs ${this.percentPipe.transform(worst.rate / 100, '1.1-1')} ${worst.name.toLowerCase()} — a huge performance gap.`,
          type: 'insight'
        });
      }
    }

    // 5. Duplicates Scan (Insight/Critical)
    if (duplicates && duplicates.total_duplicates > 10) {
      insights.push({
        title: 'Duplicate Entry Risk',
        description: `Found ${duplicates.total_duplicates} duplicate arrangements involving ${duplicates.admins_involved} admins. Clean up recommended to avoid double-charging.`,
        type: duplicates.total_duplicates > 50 ? 'critical' : 'insight'
      });
    }

    // 6. Processing Time Scan (Insight)
    if (processing && processing.overall_avg_processing_hours > 2) {
      insights.push({
        title: 'Delayed Execution',
        description: `Average arrangement processing time is ${processing.overall_avg_processing_hours.toFixed(1)} hours. Optimization could improve same-day recovery.`,
        type: 'insight'
      });
    }

    // Sort: Critical first, then Insight
    return insights.sort((a, b) => {
      if (a.type === 'critical' && b.type === 'insight') return -1;
      if (a.type === 'insight' && b.type === 'critical') return 1;
      return 0;
    }).slice(0, 4); // Show top 4 most impactful
  });


  scheduledRecoveredSeries = computed<IBarChartSeries[]>(() => {
    const data = this.monthlyScheduledRecoveredData();
    return [
      {
        key: 'scheduled',
        name: 'Scheduled Amount',
        color: '#4F46E5',
        barFillStyle: 'gradient',
        gradientStartColor: '#60A5FA',
        gradientEndColor: '#2563EB',
        data: data.map(d => ({ category: (d as any).category, value: d.bottomValue || 0, label: d.label }))
      },
      {
        key: 'recovered',
        name: 'Total Recovered Amount',
        color: '#EC4899',
        barFillStyle: 'gradient',
        gradientStartColor: '#F472B6',
        gradientEndColor: '#DB2777',
        data: data.map(d => ({ category: (d as any).category, value: d.topValue || 0, label: d.label }))
      }
    ];
  });

  successVsFailureBySlotData = computed<MultiBarData[]>(() => {
    const stats = this.paymentOutcomesStats();
    if (!stats || !stats.by_slot) return [];

    return [
      {
        category: 'Morning',
        groups: [
          { label: 'Success', value: stats.by_slot?.morning?.successful || 0, color: '#10B981' },
          { label: 'Failure', value: stats.by_slot?.morning?.failed || 0, color: '#EF4444' }
        ]
      },
      {
        category: 'Afternoon',
        groups: [
          { label: 'Success', value: stats.by_slot?.afternoon?.successful || 0, color: '#10B981' },
          { label: 'Failure', value: stats.by_slot?.afternoon?.failed || 0, color: '#EF4444' }
        ]
      },
      {
        category: 'Evening', // API uses evening, but screenshot/mock uses Night. I'll map evening to Night.
        groups: [
          { label: 'Success', value: stats.by_slot?.evening?.successful || 0, color: '#10B981' },
          { label: 'Failure', value: stats.by_slot?.evening?.failed || 0, color: '#EF4444' }
        ]
      }
    ];
  });

  chartRows = computed<ChartRowConfig[]>(() => {
    return [
      {
        cols: [
          {
            sectionId: ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE,
            title: 'Monthly Recovery Performance',
            subtitle: 'Tracking Recovery Trends Across Each Month',
            type: 'bar-line',
            loading: this.isRecoveryRateLoading(),
            hasData: this.hasRecoveryData,
            data: this.recoveryPerformanceData(),
            config: {
              valueKey: 'recovered_amount',
              labelKey: 'month_short',
              lines: [{ key: 'recovery_rate', label: 'Recovery Rate', color: '#F59E0B', axis: 'right' }],
              legendData: this.recoveryRateLegendData,
              legendGap: 60,
              tooltipData: [{ label: 'Recovered', key: 'recovered_amount', prefix: '$' }, { label: 'Rate', key: 'recovery_rate', type: 'percent', prefix: '' }]
            }
          },
          {
            sectionId: ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES,
            title: 'Arrangement Types',
            subtitle: 'Slot-wise arrangement overview.',
            type: 'pie',
            loading: this.isPaymentOutcomesLoading(),
            hasData: this.hasArrangementTypesData,
            data: this.arrangementTypesData(),
            config: {
              type: 'slices',
              isDonut: true,
              width: 250,
              height: 250,
            }
          }
        ]
      },
      {
        cols: [
          {
            sectionId: ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME,
            title: 'Success vs Failure by Time Slot',
            subtitle: 'Performance distribution across different time intervals',
            type: 'multi-horizontal-bar',
            loading: this.isPaymentOutcomesLoading(),
            hasData: this.successVsFailureBySlotData()?.some(d => d.groups?.some(g => g.value > 0)) ?? false,
            data: this.successVsFailureBySlotData(),
            config: { showLegend: true, leftMargin: 100, barSpacing: 8, height: 300, showTooltip: true }
          },
          {
            sectionId: ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT,
            title: `${this.titlePrefix()} Scheduled Vs Recovered Amount`,
            subtitle: 'Planned vs actual recovery comparison.',
            type: 'comprehensive-bar',
            hasData: this.monthlyScheduledRecoveredData()?.some(d => (d.bottomValue || 0) > 0 || (d.topValue || 0) > 0) ?? false,
            data: this.scheduledRecoveredSeries(),
            config: {
              height: 360,
              barRadius: 6,
              barWidth: 0.85,
              barGap: 0,
              groupGap: 0.3,
              showYAxisGrid: true,
              showXAxisLine: true,
              xAxis: {
                tickFormat: (d: any) => {
                  const unit = this.unit();
                  const parsed = this.normalizeDate(d);
                  if (unit === 'week') {
                    const [year, week] = d.split('-');
                    return `Wk ${week}`;
                  }
                  if (unit === 'day') return parsed.format('MMM DD');
                  if (unit === 'year') return parsed.format('YYYY');
                  return parsed.format('MMM YY');
                }
              }
            }
          }
        ]
      },
      {
        cols: [
          {
            sectionId: ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE,
            title: 'Success Rate Comparison By Type',
            subtitle: 'Compare success performance across different operational types.',
            type: 'line',
            loading: this.isSuccessRateTrendLoading(),
            hasData: this.hasSuccessRateData,
            data: {
              overall: this.successRateOverall(),
              recurring: this.successRateRecurring(),
              partial: this.successRatePartial()
            },
            config: {
              unit: this.unit(),
              primaryLineColor: '#3b82f6',
              secondaryLineColor: '#10b981',
              tertiaryLineColor: '#ef4444'
            },
            footerType: 'success-legend'
          },
          {
            sectionId: ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES,
            title: 'Arrangement Categories',
            subtitle: 'Breakdown of available arrangement categories.',
            type: 'data-progress',
            hasData: this.arrangementTypesRichData()?.some(d => (Number(d.count) || 0) > 0) ?? false,
            data: this.arrangementTypesRichData(),
            config: { mode: 'rich', heightClass: 'h-2.5', trackColor: 'bg-gray-100 dark:bg-surface-0/60' }
          }
        ]
      },
      {
        cols: [
          {
            sectionId: ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT,
            title: 'Recovery Value by Arrangement Attempt',
            subtitle: 'Cumulative recovery amount broken down by sequence of attempts.',
            type: 'lollipop',
            data: this.recoveryAttemptsDataSignal(),
            loading: this.isRecoveryByAttemptsLoading(),
            hasData: (this.recoveryAttemptsDataSignal()?.some(d => (d.value || 0) > 0)) ?? false,
            config: {
              margin: { top: 40, right: 30, bottom: 40, left: 60 },
              scaleType: 'logarithmic'
            },
            footerType: 'actions'
          },
          {
            sectionId: ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING,
            title: 'Recovery by Execution Timing',
            subtitle: 'Analyze recovery performance across different execution timeframes.',
            type: 'polar',
            data: this.recoveryTimingData(),
            hasData: this.recoveryTimingData()?.some(d => (d.value || 0) > 0) ?? false,
            config: { valueType: 'currency', scaleType: 'logarithmic' },
            footerType: 'insights'
          }
        ]
      }
    ];
  });

  apiFooterActions = signal<string[] | null>(null);

  footerActions = computed(() => {
    const apiActions = this.apiFooterActions();
    if (apiActions && apiActions.length > 0) {
      return apiActions;
    }
    return [
      'Perform root cause analysis before 2nd attempt for high-value cases.',
      'Escalate at 3+ arrangements to senior recovery team.',
      'Stop at 5+ arrangements and move to specialist external agency.',
      '$60K monthly increase.'
    ];
  });

  footerInsights = computed(() => {
    return this.trendsService.calculateTimingInsights(this.recoveryTimingData());
  });

  successRateLegend = signal([
    { label: 'Overall Success Rate', color: 'bg-blue-500' },
    { label: 'Recurring Success Rate%', color: 'bg-emerald-500' },
    { label: 'Partial Success Rate%', color: 'bg-red-500' }
  ]);

  yoyMetricConfigs = signal([
    { label: 'Recovered Amount', key: 'recoveredAmount', type: 'currency' },
    { label: 'Rate %', key: 'rate', type: 'percent', colorClass: 'text-emerald-500' },
    { label: 'Count/Success', key: 'countSuccess', type: 'default', colorClass: 'text-slate-600 dark:text-slate-400' },
    { label: 'Trend', key: 'trend', type: 'trend', colorClass: '' }
  ]);

  private getTrendColorClass(direction: string | undefined): string {
    switch (direction) {
      case 'improving': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
      case 'declining': return 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
    }
  }

  private subscriptions: Subscription[] = [];

  // Mock Data for new charts
  recoveryPerformanceData = signal<BarChartData[]>([]);

  arrangementTypesData = signal<any[]>([]);

  recoveryRateLegendData: LegendData[] = [
    { label: 'Total Recovered Amount', color: '#8b5cf6' },
    { label: 'Recovery Rate', color: '#F59E0B' }
  ];

  // Dynamic Data for isometric chart
  scheduledVsRecoveredData = computed<IsometricBarData[]>(() => {
    const stats = this.paymentOutcomesStats();
    if (!stats || !stats.by_slot) return [];

    return [
      {
        label: 'Morning',
        success: stats.by_slot.morning.successful || 0,
        failure: stats.by_slot.morning.failed || 0
      },
      {
        label: 'Afternoon',
        success: stats.by_slot.afternoon.successful || 0,
        failure: stats.by_slot.afternoon.failed || 0
      },
      {
        label: 'Night', // Mapping 'evening' to 'Night' for the isometric chart
        success: stats.by_slot.evening.successful || 0,
        failure: stats.by_slot.evening.failed || 0
      }
    ];
  });

  // Dynamic Data for stacked bar chart
  /**
   * Normalizes various date strings from API (YYYY, YYYY-MM, or YYYY-WW) 
   * to a Dayjs object for robust parsing and sorting.
   */
  private normalizeDate(dateStr: string): dayjs.Dayjs {
    return parseToUtc(dateStr, this.unit());
  }

  monthlyScheduledRecoveredData = computed<StackedBarData[]>(() => {
    const records = this.recoveryStatusRecords();
    if (!records || records.length === 0) return [];

    // Sort records chronologically before mapping
    const sortedRecords = [...records].sort((a, b) => {
      const dateA = this.normalizeDate(a.unit_date).unix();
      const dateB = this.normalizeDate(b.unit_date).unix();
      return dateA - dateB;
    });

    return sortedRecords.map(r => {
      const date = this.normalizeDate(r.unit_date);
      const unit = this.unit();
      let label: string;
      if (unit === 'week') {
        const [year, week] = r.unit_date.split('-');
        label = `Week ${week}, ${year}`;
      } else if (unit === 'month') {
        label = date.format('MMM YYYY');
      } else if (unit === 'year') {
        label = date.format('YYYY');
      } else {
        label = date.format('MMM DD, YYYY');
      }
      return {
        category: r.unit_date,
        label,
        bottomValue: (Number(r.recovered_amount) || 0) + (Number(r.non_recovered_amount) || 0),
        topValue: Number(r.recovered_amount) || 0
      };
    });
  });

  // Success Rate Data
  successRateOverall = signal<any[]>([]);
  successRateRecurring = signal<any[]>([]);
  successRatePartial = signal<any[]>([]);

  // Dynamic Data for Rich Arrangement Types
  arrangementTypesRichData = computed<ProgressBarData[]>(() => {
    const current = this.creationTrendStats();
    const previous = this.previousCreationTrendStats();

    if (!current) return [];

    const total = current.total_created || 1;
    const recurringValue = Math.round((current.recurring_created / total) * 100);
    const partialValue = Math.round((current.partial_created / total) * 100);

    const recurringGrowth = this.trendsService.calculatePercentageChange(current.recurring_created, previous?.recurring_created || 0);
    const partialGrowth = this.trendsService.calculatePercentageChange(current.partial_created, previous?.partial_created || 0);

    const formatGrowth = (val: number | null) => {
      if (val === null) return undefined;
      return `${val >= 0 ? '+' : ''}${(val * 100).toFixed(1)}%`;
    };

    return [
      {
        label: 'Recurring',
        description: 'Standard automated schedules',
        value: recurringValue,
        color: '#6366f1',
        icon: 'refresh-ccw',
        count: current.recurring_created,
        growth: formatGrowth(recurringGrowth),
        growthTrend: recurringGrowth !== null ? (recurringGrowth >= 0 ? 'up' : 'down') : undefined
      },
      {
        label: 'Partial',
        description: 'Standard automated schedules',
        value: partialValue,
        color: '#10b981',
        icon: 'refresh-ccw',
        count: current.partial_created,
        growth: formatGrowth(partialGrowth),
        growthTrend: partialGrowth !== null ? (partialGrowth >= 0 ? 'up' : 'down') : undefined
      }
    ];
  });

  // Dynamic Signal for Recovery Attempts
  recoveryAttemptsDataSignal = signal<LollipopChartData[]>([]);
  // Note: For the 'bar' item, I'll pass a specific color in the component if needed, 
  // or the component uses the passed color for the bar.

  // Dynamic Signal for Recovery by Execution Timing (Polar Area)
  recoveryTimingData = signal<PolarChartData[]>([
    { label: 'Same Day', value: 0, color: '#10b981' }, // Green
    { label: '1-7 Days', value: 0, color: '#ef4444' }, // Red
    { label: '8-14 Days', value: 0, color: '#fbbf24' }, // Orange
    { label: '15-30 Days', value: 0, color: '#3b82f6' }, // Blue
    { label: '31+ Days', value: 0, color: '#a855f7' }  // Purple
  ]);

  // Year-Over-Year Accordion Data
  public expandedYear: number | null = null;

  yearOverYearData: YearlyPerformanceData[] = [];

  toggleYear(year: number): void {
    if (this.expandedYear === year) {
      this.expandedYear = null;
    } else {
      this.expandedYear = year;
    }
  }

  /**
   * Initializes YoY performance data with a static date range (2020 to present)
   * as per requirement. This method is called once in ngOnInit and does not
   * react to global date picker changes.
   */
  private initYearlyPerformanceData(): void {
    const startTime = '2020-01-01';
    const endTime = dayjs().format('YYYY-MM-DD');

    const payload = {
      start_time: startTime,
      end_time: endTime,
      unit: 'month' as const,
      is_dummy: 0 as const
    };

    const recoverySub = this.arrangmentCronApiService.getRecoveryStatus(payload, { showLoader: false });
    const contractSub = this.arrangmentCronApiService.getContractDetails(payload, { showLoader: false });

    forkJoin([recoverySub, contractSub]).subscribe({
      next: ([recoveryResponse, contractResponse]) => {
        const recoveryRecords = recoveryResponse?.records || [];
        const contracts = contractResponse?.contracts || [];

        // Group recovery records by year
        const yearlyMap = new Map<number, {
          recoveredAmount: number;
          recoveredCount: number;
          scheduledAmount: number;
          monthlyData: MonthlyPerformanceData[];
        }>();

        recoveryRecords.forEach(record => {
          const date = dayjs(record.unit_date);
          const year = date.year();
          const monthName = date.format('MMMM');

          if (!yearlyMap.has(year)) {
            yearlyMap.set(year, { recoveredAmount: 0, recoveredCount: 0, scheduledAmount: 0, monthlyData: [] });
          }

          const yearData = yearlyMap.get(year)!;
          const recovered_amount = Number(record.recovered_amount) || 0;
          const recovered_count = Number(record.recovered_count) || 0;
          const scheduled_amount = (Number(record.recovered_amount) || 0) + (Number(record.non_recovered_amount) || 0);

          yearData.recoveredAmount += recovered_amount;
          yearData.recoveredCount += recovered_count;
          yearData.scheduledAmount += scheduled_amount;

          // Store monthly data for later processing once we have arrangement counts
          yearData.monthlyData.push({
            month: monthName,
            recoveredAmount: recovered_amount,
            recoveredCount: recovered_count,
            scheduledAmount: scheduled_amount,
            rate: Number((scheduled_amount > 0 ? (recovered_amount / scheduled_amount) * 100 : 0).toFixed(2)),
            countSuccess: "", // Will fill later
            trend: 0,
            trendDirection: 'up'
          } as any);
        });

        // Arrangement count per year and month
        const arrangementCountMap = new Map<string, number>(); // "YYYY" or "YYYY-MM"
        contracts.forEach(contract => {
          const entries = contract.entry_details || [];
          const processedDates = new Set<string>(); // Prevent double-counting same arrangement in same unit

          entries.forEach(entry => {
            const date = dayjs(entry.arrangement_date);
            const yearStr = date.format('YYYY');
            const monthStr = date.format('YYYY-MM');

            if (!processedDates.has(yearStr)) {
              arrangementCountMap.set(yearStr, (arrangementCountMap.get(yearStr) || 0) + 1);
              processedDates.add(yearStr);
            }
            if (!processedDates.has(monthStr)) {
              arrangementCountMap.set(monthStr, (arrangementCountMap.get(monthStr) || 0) + 1);
              processedDates.add(monthStr);
            }
          });
        });

        // Finalize yearly data
        const finalData: YearlyPerformanceData[] = [];
        const years = Array.from(yearlyMap.keys()).sort((a, b) => b - a);

        years.forEach((year, index) => {
          const data = yearlyMap.get(year)!;
          const prevYear = year - 1;
          const prevData = yearlyMap.get(prevYear);

          const arrangementCount = arrangementCountMap.get(year.toString()) || 1;
          const rate = data.scheduledAmount > 0 ? (data.recoveredAmount / data.scheduledAmount) * 100 : 0;
          const prevRate = (prevData && prevData.scheduledAmount > 0) ? (prevData.recoveredAmount / prevData.scheduledAmount) * 100 : 0;

          const trend = rate - prevRate;

          // Process monthly data with counts
          const processedMonthly = data.monthlyData.map(m => {
            const monthKey = dayjs(`${year}-${m.month}-01`, 'YYYY-MMMM-DD').format('YYYY-MM');
            const mArrCount = arrangementCountMap.get(monthKey) || 1;
            return {
              ...m,
              countSuccess: `${(m as any).recoveredCount.toLocaleString('en-US')} / ${mArrCount.toLocaleString('en-US')}`
            };
          });

          finalData.push({
            year,
            recoveredAmount: data.recoveredAmount,
            rate: Number(rate.toFixed(2)),
            countSuccess: `${data.recoveredCount.toLocaleString('en-US')} / ${arrangementCount.toLocaleString('en-US')}`,
            trend: Number(Math.abs(trend).toFixed(2)),
            trendDirection: trend >= 0 ? 'up' : 'down',
            monthlyData: processedMonthly as any
          });
        });

        // Calculate Monthly Trends (Comparison with same month previous year)
        const monthlyRateMap = new Map<string, number>();
        finalData.forEach(y => {
          y.monthlyData.forEach(m => {
            monthlyRateMap.set(`${y.year}-${m.month}`, m.rate);
          });
        });

        finalData.forEach(y => {
          y.monthlyData.forEach(m => {
            const prevYearRate = monthlyRateMap.get(`${y.year - 1}-${m.month}`);
            if (prevYearRate !== undefined) {
              const diff = m.rate - prevYearRate;
              m.trend = Number(Math.abs(diff).toFixed(2));
              m.trendDirection = diff >= 0 ? 'up' : 'down';
            } else {
              m.trend = 0;
              m.trendDirection = 'up';
            }
          });
        });

        this.yearOverYearData = finalData;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error in initYearlyPerformanceData:', err);
        this.cdr.markForCheck();
      }
    });
  }

  constructor() {
    effect(() => {
      this.recoveryStatusStats();
      this.successRateTrendStats();
      this.paymentOutcomesStats();
      this.recoveryStatusRecords();
      this.creationTrendStats();
      this.recoveryRateStats();
      this.processingTimeStats();
      this.duplicatesStats();
      this.recoveryAttemptsDataSignal();
      this.recoveryTimingData();
      untracked(() => this.cdr.markForCheck());
    });

    effect(() => {
      this.isRecoveryStatusLoading();
      this.isSuccessRateTrendLoading();
      this.isRecoveryRateLoading();
      this.isPaymentOutcomesLoading();
      this.isProcessingTimeLoading();
      this.isDuplicatesLoading();
      this.isRecoveryByAttemptsLoading();
      this.isLoadingCreationTrend();
      this.isPreviousDataLoading();
      untracked(() => this.cdr.markForCheck());
    });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(
            catalog as any[],
            this.PAGE_KEY,
            activeProductPid,
          );
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<ArrangementCronTrendsSectionId>();
          keys.forEach((k) => {
            const sectionId = resolveWidgetKeyToSectionId(k, this.WIDGET_KEY_TO_SECTION);
            if (sectionId != null && isArrangementCronTrendsSectionId(sectionId)) {
              pinned.add(sectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<ArrangementCronTrendsSectionId, number>();
            for (const sec of Object.values(ArrangementCronTrendsSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    effect(() => {
      const startDate = this.startDate();
      const endDate = this.endDate();
      const unit = this.unit(); // Explicitly track unit change

      if (startDate && endDate) {
        if (
          this.showSection(ArrangementCronTrendsSectionId.STATS) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_AMOUNT) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS) ||
          this.showSection(ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE) ||
          this.showSection(ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT)
        ) {
          this.getRecoveryStatus();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE) ||
          this.showSection(ArrangementCronTrendsSectionId.STATS) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_AMOUNT)
        ) {
          this.getSuccessRateTrend();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_AMOUNT)
        ) {
          this.getRecoveryRate();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME) ||
          this.showSection(ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS)
        ) {
          this.getPaymentOutcomesBySlot();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS)
        ) {
          this.getProcessingTime();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES) ||
          this.showSection(ArrangementCronTrendsSectionId.STATS) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_AMOUNT)
        ) {
          this.getCreationTrend();
        }
        if (this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS)) {
          this.getDuplicatesStats();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING)
        ) {
          this.getRecoveryByAttempts();
        }
        if (
          this.showSection(ArrangementCronTrendsSectionId.STATS) ||
          this.showSection(ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS) ||
          this.showSection(ArrangementCronTrendsSectionId.RECOVERY_AMOUNT)
        ) {
          this.getPreviousPeriodData(startDate, endDate, unit);
        }
        if (this.showSection(ArrangementCronTrendsSectionId.YOY_PERFORMANCE)) {
          this.initYearlyPerformanceData();
        }
      }
    });
  }

  ngOnInit(): void {
    this._initLayout();
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    this.datePickerService.updateDateRange(event);
  }

  private getPreviousPeriodData(startDate: string, endDate: string, unit: string): void {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const duration = end.diff(start, 'day') + 1;

    // Shift the date range back by the same duration
    const prevStart = start.subtract(duration, 'day').format('YYYY-MM-DD');
    const prevEnd = start.subtract(1, 'day').format('YYYY-MM-DD');

    const payload = {
      start_time: prevStart,
      end_time: prevEnd,
      unit: unit as any,
      is_dummy: 0 as const
    };

    this.isPreviousDataLoading.set(true);
    const sub = forkJoin({
      recovery: this.arrangmentCronApiService.getRecoveryStatus(payload, { showLoader: false }).pipe(catchError((err) => { console.error(err); return of(null); })),
      success: this.arrangmentCronApiService.getSuccessRateTrend(payload, { showLoader: false }).pipe(catchError((err) => { console.error(err); return of(null); })),
      creation: this.arrangmentCronApiService.getCreationTrend(payload, { showLoader: false }).pipe(catchError((err) => { console.error(err); return of(null); })),
      recoveryRate: this.arrangmentCronApiService.getRecoveryRate(payload, { showLoader: false }).pipe(catchError((err) => { console.error(err); return of(null); }))
    }).pipe(finalize(() => this.isPreviousDataLoading.set(false)))
      .subscribe({
        next: (results) => {
          if (results.recovery?.stats) this.previousRecoveryStatusStats.set(results.recovery.stats);
          if (results.success?.stats) this.previousSuccessRateTrendStats.set(results.success.stats);
          if (results.creation?.stats) this.previousCreationTrendStats.set(results.creation.stats);
          if (results.recoveryRate?.stats) this.previousRecoveryRateStats.set(results.recoveryRate.stats);
          this.cdr.markForCheck();
        }
      });

    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private getRecoveryStatus(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const unit = this.unit();

    if (!startDate || !endDate) return;

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    // ArrangmentCronApiService handles timezone and is_dummy
    const payload = {
      start_time: startTime,
      end_time: endTime,
      unit: unit as any,
      is_dummy: 0 as const
    };

    // Use ArrangmentCronApiService
    this.isRecoveryStatusLoading.set(true);
    const sub = this.arrangmentCronApiService.getRecoveryStatus(payload, { showLoader: false })
      .pipe(finalize(() => this.isRecoveryStatusLoading.set(false)))
      .subscribe({
        next: (response: RecoveryStatusAPI) => {
          if (response?.stats) {
            this.recoveryStatusStats.set(response.stats);
          }
          if (response?.records) {
            this.recoveryStatusRecords.set(response.records);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching recovery status:', error);
          this.cdr.markForCheck();
        }
      });

    this.subscriptions.push(sub);
  }

  private getPaymentOutcomesBySlot(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const unit = this.unit();

    if (!startDate || !endDate) return;

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    const payload = {
      start_time: startTime,
      end_time: endTime,
      unit: 'day' as const,
      is_dummy: 0 as const
    };

    this.isPaymentOutcomesLoading.set(true);
    const sub = this.arrangmentCronApiService.getPaymentOutcomesBySlot(payload, { showLoader: false })
      .pipe(finalize(() => this.isPaymentOutcomesLoading.set(false)))
      .subscribe({
        next: (response: PaymentOutcomesBySlotAPI) => {
          if (response?.stats) {
            this.paymentOutcomesStats.set(response.stats);

            // Map to Pie Chart Data
            // Colors: Morning (Orange), Afternoon (Pink), Evening (Purple)
            this.arrangementTypesData.set([
              {
                label: 'Morning',
                value: response.stats.by_slot?.morning?.total || 0,
                color: '#F59E0B'
              },
              {
                label: 'Afternoon',
                value: response.stats.by_slot?.afternoon?.total || 0,
                color: '#EC4899'
              },
              {
                label: 'Evening',
                value: response.stats.by_slot?.evening?.total || 0,
                color: '#7C3AED'
              }
            ]);
          } else {
            this.paymentOutcomesStats.set(null);
            this.arrangementTypesData.set([]);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching payment outcomes by slot:', error);
          this.arrangementTypesData.set([]);
          this.cdr.markForCheck();
        }
      });

    this.subscriptions.push(sub);
  }

  private getSuccessRateTrend(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const unit = this.unit();

    if (!startDate || !endDate) return;

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    const payload = {
      start_time: startTime,
      end_time: endTime,
      unit: unit as any,
      is_dummy: 0 as const
    };

    this.isSuccessRateTrendLoading.set(true);
    const sub = this.arrangmentCronApiService.getSuccessRateTrend(payload, { showLoader: false })
      .pipe(finalize(() => this.isSuccessRateTrendLoading.set(false)))
      .subscribe({
        next: (response: SuccessRateTrendAPI) => {
          if (response?.stats) {
            this.successRateTrendStats.set(response.stats);
          }
          if (response?.records) {
            const normalizedRecords = [...response.records].sort((a, b) =>
              this.normalizeDate(a.unit_date).unix() - this.normalizeDate(b.unit_date).unix()
            );

            this.successRateOverall.set(normalizedRecords.map(r => ({ start: this.normalizeDate(r.unit_date).format('YYYY-MM-DD'), value: r.overall_success_rate, key: 'Overall Success Rate' })));
            this.successRateRecurring.set(normalizedRecords.map(r => ({ start: this.normalizeDate(r.unit_date).format('YYYY-MM-DD'), value: r.recurring_success_rate, key: 'Recurring Success Rate%' })));
            this.successRatePartial.set(normalizedRecords.map(r => ({ start: this.normalizeDate(r.unit_date).format('YYYY-MM-DD'), value: r.partial_success_rate, key: 'Partial Success Rate%' })));
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching success rate trend:', error);
          this.cdr.markForCheck();
        }
      });

    this.subscriptions.push(sub);
  }

  get hasRecoveryData(): boolean {
    const data = this.recoveryPerformanceData();
    return !!data && data.length > 0 && data.some(d => (Number(d['recovered_amount']) || 0) > 0);
  }

  get hasSuccessRateData(): boolean {
    const overall = this.successRateOverall();
    const recurring = this.successRateRecurring();
    const partial = this.successRatePartial();
    const hasOverall = overall?.length > 0 && overall.some((d: any) => (Number(d.value) || 0) > 0);
    const hasRecurring = recurring?.length > 0 && recurring.some((d: any) => (Number(d.value) || 0) > 0);
    const hasPartial = partial?.length > 0 && partial.some((d: any) => (Number(d.value) || 0) > 0);
    return hasOverall || hasRecurring || hasPartial;
  }

  get hasArrangementTypesData(): boolean {
    const data = this.arrangementTypesData();
    return !!data && data.length > 0 && data.some(d => (Number(d.value) || 0) > 0);
  }

  private getRecoveryRate(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    // Default to 'month' for the Monthly Performance chart to show actual trends
    const unit = 'month';

    if (!startDate || !endDate) return;

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    // ArrangmentCronApiService payload structure
    const payload = {
      start_time: startTime,
      end_time: endTime,
      unit: unit as any,
      is_dummy: 0 as const
    };

    this.isRecoveryRateLoading.set(true);
    const sub = this.arrangmentCronApiService.getRecoveryRate(payload, { showLoader: false })
      .pipe(finalize(() => this.isRecoveryRateLoading.set(false)))
      .subscribe({
        next: (response: RecoveryRateAPI) => {
          if (response?.stats) {
            this.recoveryRateStats.set(response.stats);
          }
          if (response?.records) {
            const mapped = response.records.map(r => ({
              start: r.start,
              end: r.end,
              month_short: r.unit_date,
              recovered_amount: Number(r.recovered_amount) || 0,
              recovery_rate: Number(r.recovery_rate) || 0
            }));
            this.recoveryPerformanceData.set(mapped);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching recovery rate:', error);
          this.cdr.markForCheck();
        }
      });

    this.subscriptions.push(sub);
  }

  // kept for reference but not active if commented out in original
  private getCreationTrend(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const unit = this.unit();

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    const apiUnit: 'day' | 'week' | 'month' | 'quarter' | 'year' | undefined =
      unit === 'hour' ? 'day' : unit as 'day' | 'week' | 'month' | 'quarter' | 'year';

    this.isLoadingCreationTrend.set(true);
    this.arrangmentCronApiService.getCreationTrend(
      {
        is_dummy: 0,
        start_time: startTime,
        end_time: endTime,
        unit: apiUnit
      },
      { showLoader: false, cache: false }
    ).subscribe({
      next: (response) => {
        this.isLoadingCreationTrend.set(false);
        this.creationTrend.set(response);
        if (response?.stats) {
          this.creationTrendStats.set(response.stats);
        }
      },
      error: (error) => {
        this.isLoadingCreationTrend.set(false);
        console.error('Error fetching creation trend:', error);
      }
    });
  }

  private getProcessingTime(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    const unit = this.unit();

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const apiUnit = unit === 'hour' ? 'day' : unit as any;

    this.isProcessingTimeLoading.set(true);
    this.arrangmentCronApiService.getProcessingTime(
      { start_time: startTime, end_time: endTime, unit: apiUnit, is_dummy: 0 },
      { showLoader: false, cache: true }
    ).subscribe({
      next: (response) => {
        this.isProcessingTimeLoading.set(false);
        if (response?.stats) {
          this.processingTimeStats.set(response.stats);
        }
      },
      error: (error) => {
        this.isProcessingTimeLoading.set(false);
        console.error('Error fetching processing time:', error);
      }
    });
  }

  private getDuplicatesStats(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();

    const startTime = dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    this.isDuplicatesLoading.set(true);
    this.arrangmentCronApiService.getDuplicatesSummaryStats(
      { start_time: startTime, end_time: endTime, is_dummy: 0 },
      { showLoader: false, cache: true }
    ).subscribe({
      next: (response) => {
        this.isDuplicatesLoading.set(false);
        this.duplicatesStats.set(response);
      },
      error: (error) => {
        this.isDuplicatesLoading.set(false);
        console.error('Error fetching duplicates stats:', error);
      }
    });
  }

  private getRecoveryByAttempts(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) return;

    const payload = {
      start_time: dayjs.utc(startDate).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      end_time: dayjs.utc(endDate).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      is_dummy: 0 as const,
      filters: {} // Remove restrictive filter to get all contracts in period
    };

    this.isRecoveryByAttemptsLoading.set(true);
    this.arrangmentCronApiService.getContractDetails(payload, { showLoader: false })
      .pipe(finalize(() => {
        this.isRecoveryByAttemptsLoading.set(false);
      }))
      .subscribe({
        next: (response: any) => {
          const contracts = response?.contracts || [];

          // Set dynamic footer actions if provided by API
          if (response?.actions && Array.isArray(response.actions)) {
            this.apiFooterActions.set(response.actions);
          } else if (response?.recommendations && Array.isArray(response.recommendations)) {
            this.apiFooterActions.set(response.recommendations);
          }

          let first = 0, secondThird = 0, fourthFifth = 0, sixthPlus = 0;

          // Timing buckets
          let sameDay = 0, oneToSeven = 0, eightToFourteen = 0, fifteenToThirty = 0, thirtyOnePlus = 0;

          contracts.forEach((contract: any) => {
            const entries = [...(contract.entry_details || [])];
            if (entries.length === 0) return;

            // Sort by arrangement date ascending to determine attempts sequence
            entries.sort((a, b) => dayjs(a.arrangement_date).unix() - dayjs(b.arrangement_date).unix());

            entries.forEach((entry, index) => {
              // 1. Attempts Logic
              const attemptNo = index + 1;
              const amount = Number(entry.amount) || 0;

              if (attemptNo === 1) first += amount;
              else if (attemptNo <= 3) secondThird += amount;
              else if (attemptNo <= 5) fourthFifth += amount;
              else sixthPlus += amount;

              // 2. Timing Logic (Days to Execution)
              const creationDate = dayjs(entry.creation_date);
              const arrangementDate = dayjs(entry.arrangement_date);
              const daysToExecution = arrangementDate.diff(creationDate, 'day');

              if (daysToExecution <= 0) sameDay += amount;
              else if (daysToExecution <= 7) oneToSeven += amount;
              else if (daysToExecution <= 14) eightToFourteen += amount;
              else if (daysToExecution <= 30) fifteenToThirty += amount;
              else thirtyOnePlus += amount;
            });
          });

          // Update Attempts Signal
          const attemptsTrendData = [
            { label: '1st Arr', value: first, color: '#10b981', type: 'line' as const },
            { label: '2-3rd Arr', value: secondThird, color: '#fbbf24', type: 'line' as const },
            { label: '4-5th Arr', value: fourthFifth, color: '#c084fc', type: 'bar' as const },
            { label: '6+ Arr', value: sixthPlus, color: '#3b82f6', type: 'line' as const }
          ];
          this.recoveryAttemptsDataSignal.set(attemptsTrendData);

          // Update Timing Signal
          const timingDetails = [
            { label: 'Same Day', value: sameDay, color: '#10b981' },
            { label: '1-7 Days', value: oneToSeven, color: '#ef4444' },
            { label: '8-14 Days', value: eightToFourteen, color: '#fbbf24' },
            { label: '15-30 Days', value: fifteenToThirty, color: '#3b82f6' },
            { label: '31+ Days', value: thirtyOnePlus, color: '#a855f7' }
          ];
          this.recoveryTimingData.set(timingDetails);
        },
        error: (err) => {
          console.error('Error fetching recovery by attempts and timing:', err);
          this.recoveryAttemptsDataSignal.set([]);
          this.recoveryTimingData.set([]);
        }
      });
  }

  // ─── Pin handlers ──────────────────────────────────────────────────────
  private _toSectionId(
    sec: { id: ArrangementCronTrendsSectionId } | ArrangementCronTrendsSectionId | string,
  ): ArrangementCronTrendsSectionId | null {
    if (typeof sec === 'string') {
      return isArrangementCronTrendsSectionId(sec) ? sec : null;
    }
    return sec?.id ?? null;
  }

  isAddedToCustomDashboard(
    sec: { id: ArrangementCronTrendsSectionId } | ArrangementCronTrendsSectionId | string,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pinnedSections().has(id) : false;
  }

  isAddingToCustomDashboard(
    sec: { id: ArrangementCronTrendsSectionId } | ArrangementCronTrendsSectionId | string,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pendingPinSections().has(id) : false;
  }

  canPinToCustomDashboard(
    sec: { id: ArrangementCronTrendsSectionId } | ArrangementCronTrendsSectionId | string,
  ): boolean {
    if (this.mixedMode()) return false;
    return !!this._toSectionId(sec);
  }

  addToCustomDashboard(
    sec: { id: ArrangementCronTrendsSectionId } | ArrangementCronTrendsSectionId | string,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toaster?.info?.(
        'Pinning Arrangement Cron trends sections to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Layout editor (public entry for parent shell) ─────────────────────
  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: TrendsLayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      defaultColSpan: this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      ),
    }));

    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: string): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: TrendsLayoutEditorItem[] = this.SECTIONS.map((s, idx) => {
      const defaultSpan = this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      );
      return {
        id: s.id as ArrangementCronTrendsSectionId,
        title: s.title,
        visible: true,
        colSpan: defaultSpan,
        order: idx,
        defaultColSpan: defaultSpan,
      };
    });
    this.editorItems.set(defaults);
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    if (!draft.some((d) => d.visible)) {
      this.toaster?.warning?.(
        'At least one section must be visible. Toggle a section on before applying.',
      );
      return;
    }

    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.toaster?.success?.('Layout updated for Trends.');

    this.isLayoutEditorOpen.set(false);
  }

  decreaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[i.id] ?? 12,
        );
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  onEditorCardDrop(event: CdkDragDrop<TrendsLayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (!next) return;
    this.editorItems.set(next);
    this._syncLayoutFromEditor();
  }

  private _buildLayoutFromEditor(): Record<
    ArrangementCronTrendsSectionId,
    TrendsSectionLayoutItem
  > {
    const map = buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS,
      ARRANGMENT_CRON_DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
    return map as Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem>;
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem> =
      {} as Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem>;

    ArrangementCronTrendsSectionDef.forEach((s, idx) => {
      const sid = s.id as ArrangementCronTrendsSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    if (!Object.values(layout).some((l) => l.visible)) {
      ArrangementCronTrendsSectionDef.forEach((s, idx) => {
        const sid = s.id as ArrangementCronTrendsSectionId;
        layout[sid] = {
          id: sid,
          visible: true,
          colSpan: this._normalizeColSpan(ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12),
          order: idx,
        };
      });
    }

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, TrendsSectionLayoutItem> | null {
    try {
      const raw =
        localStorage.getItem(this.LAYOUT_LS_KEY) ??
        localStorage.getItem('prism_arrangment_cron_ac_trends_recovery_layout_v2') ??
        localStorage.getItem('prism_arrangment_cron_ac_trends_recovery_layout_v1');
      return raw ? this._migrateLegacyTrendsLayout(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private _migrateLegacyTrendsLayout(
    stored: Record<string, TrendsSectionLayoutItem>,
  ): Record<string, TrendsSectionLayoutItem> {
    const migrated = { ...stored };
    const legacyKeyMap: Record<string, ArrangementCronTrendsSectionId> = {
      tr_metric_cards: ArrangementCronTrendsSectionId.STATS,
      tr_critical_insights: ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS,
      tr_monthly_recovery: ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE,
      tr_arrangement_types_pie: ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES,
      tr_success_vs_failure: ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME,
      tr_scheduled_vs_recovered: ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT,
      tr_success_rate_comparison: ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE,
      tr_arrangement_types_progress: ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES,
      tr_recovery_attempts: ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT,
      tr_recovery_timing: ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING,
      tr_year_over_year: ArrangementCronTrendsSectionId.YOY_PERFORMANCE,
    };

    for (const [legacyKey, nextKey] of Object.entries(legacyKeyMap)) {
      if (migrated[legacyKey] && !migrated[nextKey]) {
        migrated[nextKey] = { ...migrated[legacyKey], id: nextKey };
      }
      delete migrated[legacyKey];
    }

    const insightLegacy = migrated['tr_insight_cards'];
    if (insightLegacy && !migrated[ArrangementCronTrendsSectionId.RECOVERY_AMOUNT]) {
      migrated[ArrangementCronTrendsSectionId.RECOVERY_AMOUNT] = {
        ...insightLegacy,
        id: ArrangementCronTrendsSectionId.RECOVERY_AMOUNT,
      };
      delete migrated['tr_insight_cards'];
    }

    for (const legacyKey of ['ac_tr_volume_trend', 'ac_tr_success_rate'] as const) {
      if (migrated[legacyKey] && !migrated[ArrangementCronTrendsSectionId.RECOVERY_AMOUNT]) {
        migrated[ArrangementCronTrendsSectionId.RECOVERY_AMOUNT] = {
          ...migrated[legacyKey],
          id: ArrangementCronTrendsSectionId.RECOVERY_AMOUNT,
        };
      }
      delete migrated[legacyKey];
    }

    const removedTrendKeys: Record<string, ArrangementCronTrendsSectionId> = {
      ac_tr_arrangement_type: ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES,
      ac_tr_success_rate_type: ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE,
      ac_tr_critical_insights_stats: ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS,
    };
    for (const [legacyKey, nextKey] of Object.entries(removedTrendKeys)) {
      if (migrated[legacyKey] && !migrated[nextKey]) {
        migrated[nextKey] = { ...migrated[legacyKey], id: nextKey };
      }
      delete migrated[legacyKey];
    }

    return migrated;
  }

  private _saveLayoutToStorage(
    layout: Record<ArrangementCronTrendsSectionId, TrendsSectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    return normalizeLayoutEditorColSpan(span);
  }

  private _persistLayoutToBackend(
    draft: ReadonlyArray<TrendsLayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) return;

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }
}
